import { useState } from "react";
import PlantHero from "@/components/PlantHero";
import { STAGE_NAMES, plantStage } from "@/lib/skincare";

export default function PlantTest() {
  const [manual, setManual] = useState(false);
  const [stage, setStage] = useState(3);
  const [streak, setStreak] = useState(5);
  const [dayNum, setDayNum] = useState(10);
  const [completedDays, setCompletedDays] = useState(10);

  const derivedStage = plantStage(streak, completedDays);
  const displayStage = manual ? stage : derivedStage;
  const score = (streak * 1.5 + completedDays * 0.4).toFixed(1);

  return (
    <div className="mobile-shell bg-[#F7F5F0] overflow-y-auto pb-10">
      <PlantHero stage={displayStage} dayNum={dayNum} streak={streak} dose={dayNum % 2 === 1 ? "10 mg" : "5 mg"} />

      <div className="px-6 mt-6 space-y-5">
        {/* Mode toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setManual(false)}
            className={`flex-1 py-2 rounded-full text-sm font-medium border transition-colors ${
              !manual ? "bg-[#2A332C] text-white border-[#2A332C]" : "bg-white text-[#6B7A70] border-[#E9EFEA]"
            }`}
          >
            Auto (formula)
          </button>
          <button
            onClick={() => setManual(true)}
            className={`flex-1 py-2 rounded-full text-sm font-medium border transition-colors ${
              manual ? "bg-[#2A332C] text-white border-[#2A332C]" : "bg-white text-[#6B7A70] border-[#E9EFEA]"
            }`}
          >
            Manual stage
          </button>
        </div>

        {/* Score / stage display */}
        <div className="p-4 rounded-2xl bg-white border border-[#E9EFEA] text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-[#6B7A70]">Score</span>
            <span className="font-semibold text-[#2A332C]">{score}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#6B7A70]">Stage</span>
            <span className="font-semibold text-[#2A332C]">{displayStage} — {STAGE_NAMES[displayStage]}</span>
          </div>
        </div>

        {manual ? (
          <Slider label="Stage" min={0} max={10} value={stage} onChange={setStage}
            hint={`${STAGE_NAMES[stage]}`} />
        ) : (
          <>
            <Slider label="Streak (days)" min={0} max={60} value={streak} onChange={setStreak}
              hint={`score contribution: ${(streak * 1.5).toFixed(1)}`} />
            <Slider label="Completed days" min={0} max={60} value={completedDays} onChange={setCompletedDays}
              hint={`score contribution: ${(completedDays * 0.4).toFixed(1)}`} />
          </>
        )}

        <Slider label="Day number" min={1} max={60} value={dayNum} onChange={setDayNum} />

        {/* Stage thresholds reference */}
        <div className="p-4 rounded-2xl bg-white border border-[#E9EFEA]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7A70] mb-3">Stage thresholds</div>
          <div className="space-y-1.5">
            {[
              [0,  "Seed",          "0"],
              [1,  "Sprout",        "≥ 3"],
              [2,  "Seedling",      "≥ 8"],
              [3,  "Young Plant",   "≥ 15"],
              [4,  "Budding",       "≥ 25"],
              [5,  "Flourishing",   "≥ 38"],
              [6,  "Blooming",      "≥ 52"],
              [7,  "Vibrant",       "≥ 65"],
              [8,  "Radiant",       "≥ 78"],
              [9,  "Luminous",      "≥ 91"],
              [10, "In Full Bloom", "≥ 105"],
            ].map(([s, name, threshold]) => (
              <div key={s} className={`flex justify-between text-sm py-1 px-2 rounded-lg ${displayStage === s ? "bg-[#E9F0E8] font-semibold text-[#2A332C]" : "text-[#6B7A70]"}`}>
                <span>{s} — {name}</span>
                <span>score {threshold}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Slider({ label, min, max, value, onChange, hint }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-[#E9EFEA]">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-sm font-medium text-[#2A332C]">{label}</span>
        <span className="text-lg font-heading font-semibold text-[#4A6B53]">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#4A6B53]"
      />
      <div className="flex justify-between text-[10px] text-[#6B7A70] mt-1">
        <span>{min}</span>
        {hint && <span className="italic">{hint}</span>}
        <span>{max}</span>
      </div>
    </div>
  );
}
